import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import STRING_KEYS from '../../../../language/keys';
import { ReferralReward } from '../../../../@types/Referral';
import { isReissueReward, isValidEmail } from '../../referrals.utils';
import style from '../style';

enum ResendMode {
  SameEmail = 'SAME_EMAIL',
  NewEmail = 'NEW_EMAIL',
}

// A canceled gift card is re-issued (a new order) rather than resent or redirected, so it gets its own copy.
const RESEND_COPY = {
  title: STRING_KEYS.REFERRALS.RESEND_DIALOG_TITLE,
  subTitle: STRING_KEYS.REFERRALS.RESEND_DIALOG_SUB_TITLE,
  sameEmail: STRING_KEYS.REFERRALS.RESEND_DIALOG_SAME_EMAIL,
  newEmail: STRING_KEYS.REFERRALS.RESEND_DIALOG_NEW_EMAIL,
  sameEmailButton: STRING_KEYS.REFERRALS.RESEND_DIALOG_BTN,
  newEmailButton: STRING_KEYS.REFERRALS.REDIRECT_DIALOG_BTN,
};

const REISSUE_COPY: typeof RESEND_COPY = {
  title: STRING_KEYS.REFERRALS.REISSUE_DIALOG_TITLE,
  subTitle: STRING_KEYS.REFERRALS.REISSUE_DIALOG_SUB_TITLE,
  sameEmail: STRING_KEYS.REFERRALS.REISSUE_DIALOG_SAME_EMAIL,
  newEmail: STRING_KEYS.REFERRALS.REISSUE_DIALOG_NEW_EMAIL,
  sameEmailButton: STRING_KEYS.REFERRALS.REISSUE_DIALOG_BTN,
  newEmailButton: STRING_KEYS.REFERRALS.REISSUE_DIALOG_BTN,
};

type ResendRewardDialogProps = {
  isOpen: boolean,
  isSubmitting: boolean,
  reward: ReferralReward,
  currentEmail: string,
  onConfirm: (newEmail?: string) => void,
  onClose: () => void,
};

export const ResendRewardDialog = ({
  isOpen,
  isSubmitting,
  reward,
  currentEmail,
  onConfirm,
  onClose,
}: ResendRewardDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [mode, setMode] = useState<ResendMode>(ResendMode.SameEmail);
  const [newEmail, setNewEmail] = useState('');
  const isRedirect = mode === ResendMode.NewEmail;
  const isNewEmailValid = isValidEmail(newEmail);
  const showEmailError = isRedirect && !!newEmail && !isNewEmailValid;
  const isConfirmDisabled = isSubmitting || (isRedirect && !isNewEmailValid);
  const copy = isReissueReward(reward) ? REISSUE_COPY : RESEND_COPY;

  const handleConfirm = () => {
    onConfirm(isRedirect ? newEmail.trim() : undefined);
  };

  return (
    <Dialog open={ isOpen } onClose={ onClose } fullWidth>
      <DialogTitle>
        { translate(copy.title, { milestone: reward.milestone }) }
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          { translate(copy.subTitle, { amount: reward.amountUsd ? `$${reward.amountUsd}` : '' }) }
        </DialogContentText>
        <RadioGroup
          value={ mode }
          onChange={ (e, value) => setMode(value === ResendMode.NewEmail ? ResendMode.NewEmail : ResendMode.SameEmail) }
          sx={ style.field.sx }
        >
          <FormControlLabel
            value={ ResendMode.SameEmail }
            control={ <Radio /> }
            label={ translate(copy.sameEmail, { email: currentEmail }) }
          />
          <FormControlLabel
            value={ ResendMode.NewEmail }
            control={ <Radio /> }
            label={ translate(copy.newEmail) }
          />
        </RadioGroup>
        { isRedirect && (
          <TextField
            autoFocus
            fullWidth
            type="email"
            label={ translate(STRING_KEYS.REFERRALS.RESEND_DIALOG_EMAIL_LABEL) }
            value={ newEmail }
            error={ showEmailError }
            helperText={ showEmailError ? translate(STRING_KEYS.REFERRALS.RESEND_DIALOG_EMAIL_INVALID) : undefined }
            onChange={ (e) => setNewEmail(e.target.value) }
            sx={ style.field.sx }
          />
        ) }
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose }>{ translate(STRING_KEYS.DIALOG.BTN_GO_BACK) }</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={ isConfirmDisabled }
          onClick={ handleConfirm }
        >
          { translate(isRedirect ? copy.newEmailButton : copy.sameEmailButton) }
        </Button>
      </DialogActions>
    </Dialog>
  );
};
